import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../Auth/AuthContext';
import {
    getAccountProfile,
    updateProfile,
    uploadAvatar,
    changeEmail as changeEmailApi,
    changePassword as changePasswordApi,
    deleteAccount as deleteAccountApi,
} from '../../utils/accountApi';
import { getApiBaseUrl } from '../../utils/api';
import { toast } from '../Toast';

const ChevronLeftIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="15,18 9,12 15,6"/>
    </svg>
);

// Resolve avatar URL (prefix with API base when relative)
function getAvatarUrl(avatar) {
    if (!avatar) return null;
    if (avatar.startsWith('http')) return avatar;
    const base = getApiBaseUrl().replace(/\/$/, '');
    return `${base}${avatar.startsWith('/') ? '' : '/'}${avatar}`;
}

// Account Page Content
function AccountPage({
    userData,
    loading,
    error,
    onUpdateName,
    onAvatarClick,
    onAvatarFileSelect,
    avatarUploadProgress,
    isUploadingAvatar,
    avatarPreviewUrl,
    avatarInputRef,
    onChangeEmailClick,
    onChangePasswordClick,
    onDeleteAccountClick,
}) {
    const [name, setName] = useState(userData?.name ?? '');
    const [isNameChanged, setIsNameChanged] = useState(false);
    const avatarUrl = getAvatarUrl(userData?.avatar);

    useEffect(() => {
        setName(userData?.name ?? '');
    }, [userData?.name]);

    const handleNameChange = (e) => {
        setName(e.target.value);
        setIsNameChanged(e.target.value !== (userData?.name ?? ''));
    };

    const handleUpdateName = () => {
        onUpdateName(name);
        setIsNameChanged(false);
    };

    const getInitial = () => {
        return userData?.name ? userData.name.charAt(0).toUpperCase() : 'U';
    };

    if (loading && !userData?.email) {
        return (
            <div style={styles.pageContent}>
                <p style={styles.pageSubtitle}>Loading account…</p>
            </div>
        );
    }

    // Progress ring outside the circle: 56px wrapper, ring at r=26 (around 48px inner circle)
    const ringSize = 56;
    const ringRadius = 26;
    const ringCircumference = 2 * Math.PI * ringRadius;
    const ringProgressOffset = ringCircumference - (avatarUploadProgress / 100) * ringCircumference;

    // Show preview image as soon as user selects file; otherwise server avatar or initial
    const displayAvatarUrl = avatarPreviewUrl || avatarUrl;

    return (
        <div style={styles.pageContent}>
            <h1 style={styles.pageTitle}>Account</h1>
            <p style={styles.pageSubtitle}>Changes to account will apply to all of your products.</p>
            {error && <p style={styles.errorText}>{error}</p>}

            {/* Avatar Section */}
            <div style={styles.section}>
                <h3 style={styles.sectionLabel}>Avatar</h3>
                <p style={styles.sectionDescription}>
                    Gravatar is the default avatar used for EarlyBird, but you can upload your own custom avatar here.
                </p>
                <div style={styles.avatarRow}>
                    <div style={styles.avatarCircleWrapper}>
                        {/* Progress ring outside the circle - only visible while uploading */}
                        {isUploadingAvatar && (
                            <svg style={styles.progressRingOuter} viewBox={`0 0 ${ringSize} ${ringSize}`}>
                                <circle
                                    cx={ringSize / 2}
                                    cy={ringSize / 2}
                                    r={ringRadius}
                                    fill="none"
                                    stroke="rgba(229,231,235,0.8)"
                                    strokeWidth="3"
                                />
                                <circle
                                    cx={ringSize / 2}
                                    cy={ringSize / 2}
                                    r={ringRadius}
                                    fill="none"
                                    stroke="#9ca3af"
                                    strokeWidth="3"
                                    strokeDasharray={ringCircumference}
                                    strokeDashoffset={ringProgressOffset}
                                    strokeLinecap="round"
                                    transform={`rotate(-90 ${ringSize / 2} ${ringSize / 2})`}
                                />
                            </svg>
                        )}
                        <div style={styles.avatarCircle}>
                            {displayAvatarUrl ? (
                                <img src={displayAvatarUrl} alt="Avatar" style={styles.avatarImage} />
                            ) : (
                                <span style={styles.avatarInitial}>{getInitial()}</span>
                            )}
                        </div>
                    </div>
                    <input
                        ref={avatarInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        style={styles.hiddenInput}
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) onAvatarFileSelect(file);
                            e.target.value = '';
                        }}
                    />
                    <button
                        type="button"
                        style={styles.changeButton}
                        onClick={onAvatarClick}
                        disabled={isUploadingAvatar}
                    >
                        Change
                    </button>
                </div>
            </div>

            {/* Name Section */}
            <div style={styles.section}>
                <h3 style={styles.sectionLabel}>Your name</h3>
                <div style={styles.inputRow}>
                    <input
                        type="text"
                        value={name}
                        onChange={handleNameChange}
                        style={styles.textInput}
                    />
                    <button
                        style={{
                            ...styles.updateButton,
                            ...(isNameChanged ? styles.updateButtonActive : {})
                        }}
                        onClick={handleUpdateName}
                        disabled={!isNameChanged}
                    >
                        Update
                    </button>
                </div>
            </div>

            {/* Email Section */}
            <div style={styles.section}>
                <h3 style={styles.sectionLabel}>Email address</h3>
                <div style={styles.emailRow}>
                    <span style={styles.emailText}>{userData?.email ?? '—'}</span>
                    <button type="button" style={styles.linkButton} onClick={onChangeEmailClick}>
                        Change email address
                    </button>
                </div>
            </div>

            {/* Password Section */}
            <div style={styles.section}>
                <h3 style={styles.sectionLabel}>Password</h3>
                <button type="button" style={styles.linkButton} onClick={onChangePasswordClick}>
                    Change password
                </button>
            </div>

            {/* Danger Zone */}
            <div style={styles.dangerSection}>
                <h3 style={styles.dangerLabel}>Danger zone</h3>
                <p style={styles.dangerDescription}>
                    This will permanently delete your entire account, including all your landing pages, conversions, and members.
                </p>
                <button type="button" style={styles.deleteButton} onClick={onDeleteAccountClick}>
                    Delete account
                </button>
            </div>
        </div>
    );
}

// Main AccountSettings Component
function AccountSettings({ onBackToDashboard }) {
    const { user: authUser, tokens, refreshUser, logout } = useAuth();
    const avatarInputRef = useRef(null);
    const [userData, setUserData] = useState({
        name: '',
        email: '',
        avatar: null,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [modal, setModal] = useState(null);
    const [avatarUploadProgress, setAvatarUploadProgress] = useState(0);
    const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
    const [avatarPreviewUrl, setAvatarPreviewUrl] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const accessToken = tokens?.accessToken;
        if (!accessToken) {
            setUserData(prev => ({ ...prev, ...authUser }));
            setLoading(false);
            return;
        }
        setLoading(true);
        getAccountProfile({ accessToken })
            .then((result) => {
                if (cancelled) return;
                if (result.ok && result.data?.user) {
                    const u = result.data.user;
                    setUserData({
                        name: u.name ?? '',
                        email: u.email ?? '',
                        avatar: u.avatar ?? null,
                    });
                } else {
                    setUserData(prev => ({ ...prev, ...authUser }));
                }
            })
            .catch(() => {
                if (!cancelled) setUserData(prev => ({ ...prev, ...authUser }));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [tokens?.accessToken, authUser?.id]);

    const handleUpdateName = async (newName) => {
        setError('');
        setBusy(true);
        const result = await updateProfile({
            accessToken: tokens?.accessToken,
            name: newName,
        });
        setBusy(false);
        if (result.ok && result.data?.user) {
            setUserData(prev => ({ ...prev, name: result.data.user.name }));
            await refreshUser();
        } else {
            setError(result.error || 'Failed to update name');
        }
    };

    const handleAvatarClick = () => {
        avatarInputRef.current?.click();
    };

    const handleAvatarFileSelect = (file) => {
        setError('');
        if (avatarPreviewUrl) {
            URL.revokeObjectURL(avatarPreviewUrl);
            setAvatarPreviewUrl(null);
        }
        const previewUrl = URL.createObjectURL(file);
        setAvatarPreviewUrl(previewUrl);
        setAvatarUploadProgress(0);
        setIsUploadingAvatar(true);
        uploadAvatar({
            accessToken: tokens?.accessToken,
            file,
            onProgress: (percent) => setAvatarUploadProgress(percent),
        }).then((result) => {
            setIsUploadingAvatar(false);
            setAvatarUploadProgress(0);
            URL.revokeObjectURL(previewUrl);
            setAvatarPreviewUrl(null);
            if (result.ok && result.data?.user) {
                setUserData(prev => ({ ...prev, avatar: result.data.user.avatar }));
                refreshUser();
                toast.success('Avatar updated successfully');
            } else {
                setError(result.error || 'Failed to update avatar');
                toast.error(result.error || 'Failed to update avatar');
            }
        }).catch(() => {
            setIsUploadingAvatar(false);
            setAvatarUploadProgress(0);
            URL.revokeObjectURL(previewUrl);
            setAvatarPreviewUrl(null);
            toast.error('Failed to update avatar');
        });
    };

    const handleChangeEmailClick = () => {
        setModal({
            type: 'email',
            newEmail: '',
            currentPassword: '',
        });
    };

    const handleChangePasswordClick = () => {
        setModal({
            type: 'password',
            currentPassword: '',
            newPassword: '',
        });
    };

    const handleDeleteAccountClick = () => {
        if (!window.confirm('Are you sure you want to permanently delete your account? This cannot be undone.')) return;
        setModal({ type: 'delete', password: '' });
    };

    const handleModalSubmit = async () => {
        if (!modal) return;
        setError('');
        setBusy(true);
        const accessToken = tokens?.accessToken;
        try {
            if (modal.type === 'email') {
                const result = await changeEmailApi({
                    accessToken,
                    newEmail: modal.newEmail,
                    currentPassword: modal.currentPassword,
                });
                setBusy(false);
                if (result.ok && result.data?.user) {
                    setUserData(prev => ({ ...prev, email: result.data.user.email }));
                    await refreshUser();
                    setModal(null);
                } else {
                    setError(result.error || 'Failed to change email');
                }
            } else if (modal.type === 'password') {
                const result = await changePasswordApi({
                    accessToken,
                    currentPassword: modal.currentPassword,
                    newPassword: modal.newPassword,
                });
                setBusy(false);
                if (result.ok) {
                    setModal(null);
                } else {
                    setError(result.error || 'Failed to change password');
                }
            } else if (modal.type === 'delete') {
                const result = await deleteAccountApi({
                    accessToken,
                    password: modal.password,
                });
                setBusy(false);
                if (result.ok) {
                    logout();
                    setModal(null);
                    if (typeof onBackToDashboard === 'function') onBackToDashboard();
                } else {
                    setError(result.error || 'Failed to delete account');
                }
            }
        } catch (e) {
            setBusy(false);
            setError('Something went wrong');
        }
    };

    return (
        <div style={styles.container}>
            

            {/* Breadcrumb */}
            <div style={styles.breadcrumb}>
                <span style={styles.breadcrumbLink} onClick={onBackToDashboard}>Settings</span>
                <span style={styles.breadcrumbSeparator}>/</span>
                <span style={{color: '#1a1a1a', fontWeight: 500}}>Account</span>
            </div>

            <div style={styles.mainContent}>
                <AccountPage
                    userData={userData}
                    loading={loading}
                    error={error}
                    onUpdateName={handleUpdateName}
                    onAvatarClick={handleAvatarClick}
                    onAvatarFileSelect={handleAvatarFileSelect}
                    avatarUploadProgress={avatarUploadProgress}
                    isUploadingAvatar={isUploadingAvatar}
                    avatarPreviewUrl={avatarPreviewUrl}
                    avatarInputRef={avatarInputRef}
                    onChangeEmailClick={handleChangeEmailClick}
                    onChangePasswordClick={handleChangePasswordClick}
                    onDeleteAccountClick={handleDeleteAccountClick}
                />
            </div>

            {modal && (
                <div style={styles.modalOverlay} onClick={() => !busy && setModal(null)}>
                    <div style={styles.modal} onClick={e => e.stopPropagation()}>
                        <h3 style={styles.modalTitle}>
                            {modal.type === 'email' && 'Change email address'}
                            {modal.type === 'password' && 'Change password'}
                            {modal.type === 'delete' && 'Delete account'}
                        </h3>
                        {error && <p style={styles.errorText}>{error}</p>}
                        {modal.type === 'email' && (
                            <>
                                <input
                                    type="email"
                                    placeholder="New email"
                                    value={modal.newEmail}
                                    onChange={e => setModal(m => ({ ...m, newEmail: e.target.value }))}
                                    style={styles.textInput}
                                />
                                <input
                                    type="password"
                                    placeholder="Current password"
                                    value={modal.currentPassword}
                                    onChange={e => setModal(m => ({ ...m, currentPassword: e.target.value }))}
                                    style={styles.textInput}
                                />
                            </>
                        )}
                        {modal.type === 'password' && (
                            <>
                                <input
                                    type="password"
                                    placeholder="Current password"
                                    value={modal.currentPassword}
                                    onChange={e => setModal(m => ({ ...m, currentPassword: e.target.value }))}
                                    style={styles.textInput}
                                />
                                <input
                                    type="password"
                                    placeholder="New password"
                                    value={modal.newPassword}
                                    onChange={e => setModal(m => ({ ...m, newPassword: e.target.value }))}
                                    style={styles.textInput}
                                />
                            </>
                        )}
                        {modal.type === 'delete' && (
                            <input
                                type="password"
                                placeholder="Enter your password to confirm"
                                value={modal.password}
                                onChange={e => setModal(m => ({ ...m, password: e.target.value }))}
                                style={styles.textInput}
                            />
                        )}
                        <div style={styles.modalActions}>
                            <button
                                type="button"
                                style={styles.cancelButton}
                                onClick={() => !busy && setModal(null)}
                                disabled={busy}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                style={styles.submitButton}
                                onClick={handleModalSubmit}
                                disabled={busy}
                            >
                                {busy ? 'Saving…' : 'Confirm'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const styles = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: '#fbfaf8',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        padding: '24px 40px'
    },
    backButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 12px',
        marginBottom: '16px',
        background: 'none',
        border: 'none',
        color: '#3b82f6',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
        borderRadius: '6px',
        transition: 'background-color 0.2s',
        width: 'fit-content'
    },
    breadcrumb: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '14px',
        color: '#666',
        marginBottom: '24px'
    },
    breadcrumbLink: {
        color: '#666',
        textDecoration: 'none',
        cursor: 'pointer',
        transition: 'color 0.2s'
    },
    breadcrumbSeparator: {
        color: '#999'
    },
    mainContent: {
        flex: 1,
        maxWidth: '600px'
    },
    pageContent: {
        width: '100%'
    },
    pageTitle: {
        fontSize: '28px',
        fontWeight: 700,
        color: '#1f2937',
        margin: '0 0 8px 0'
    },
    pageSubtitle: {
        fontSize: '14px',
        color: '#6b7280',
        margin: '0 0 32px 0'
    },
    section: {
        marginBottom: '32px'
    },
    sectionLabel: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#374151',
        margin: '0 0 8px 0'
    },
    sectionDescription: {
        fontSize: '13px',
        color: '#9ca3af',
        margin: '0 0 16px 0'
    },
    avatarRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
    },
    avatarCircleWrapper: {
        position: 'relative',
        width: '56px',
        height: '56px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    progressRingOuter: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '56px',
        height: '56px',
        pointerEvents: 'none'
    },
    avatarCircle: {
        width: '48px',
        height: '48px',
        borderRadius: '50%',
        backgroundColor: '#6b7280',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        position: 'relative'
    },
    hiddenInput: {
        display: 'none'
    },
    avatarImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    },
    avatarInitial: {
        color: '#ffffff',
        fontSize: '20px',
        fontWeight: 500
    },
    changeButton: {
        padding: '8px 16px',
        backgroundColor: '#ffffff',
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        color: '#374151',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    inputRow: {
        display: 'flex',
        gap: '12px',
        alignItems: 'center'
    },
    textInput: {
        flex: 1,
        maxWidth: '300px',
        padding: '10px 14px',
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        fontSize: '14px',
        color: '#1f2937',
        outline: 'none',
        transition: 'border-color 0.2s'
    },
    updateButton: {
        padding: '10px 20px',
        backgroundColor: '#f3f4f6',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        color: '#9ca3af',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'not-allowed',
        transition: 'all 0.2s'
    },
    updateButtonActive: {
        backgroundColor: '#1f2937',
        borderColor: '#1f2937',
        color: '#ffffff',
        cursor: 'pointer'
    },
    emailRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    },
    emailText: {
        fontSize: '14px',
        color: '#6b7280'
    },
    linkButton: {
        background: 'none',
        border: 'none',
        color: '#1f2937',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
        padding: 0,
        textDecoration: 'none'
    },
    dangerSection: {
        marginTop: '48px',
        paddingTop: '32px',
        borderTop: '1px solid #e5e7eb'
    },
    dangerLabel: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#374151',
        margin: '0 0 8px 0'
    },
    dangerDescription: {
        fontSize: '13px',
        color: '#9ca3af',
        margin: '0 0 16px 0'
    },
    deleteButton: {
        padding: '10px 20px',
        backgroundColor: '#ef4444',
        border: 'none',
        borderRadius: '6px',
        color: '#ffffff',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'background-color 0.2s'
    },
    errorText: {
        color: '#dc2626',
        fontSize: '14px',
        margin: '0 0 16px 0'
    },
    modalOverlay: {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
    },
    modal: {
        backgroundColor: '#fff',
        borderRadius: '8px',
        padding: '24px',
        maxWidth: '400px',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
    },
    modalTitle: {
        fontSize: '18px',
        fontWeight: 600,
        color: '#1f2937',
        margin: '0 0 8px 0'
    },
    modalActions: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end',
        marginTop: '8px'
    },
    cancelButton: {
        padding: '8px 16px',
        backgroundColor: '#f3f4f6',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        color: '#374151',
        fontSize: '14px',
        cursor: 'pointer'
    },
    submitButton: {
        padding: '8px 16px',
        backgroundColor: '#1f2937',
        border: 'none',
        borderRadius: '6px',
        color: '#fff',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer'
    }
};

export {AccountSettings};
